/**
 * All copy and data for the Logo & Stickers page, kept in one place (like the other static About
 * pages) so the section components stay presentational. Asset URLs are Vite `?url` imports — the same
 * string in the client and SSR builds — referenced through the `/static/site/build/static-pages/`
 * prefix the Logos view rewrites to the runtime STATIC_URL at serve time.
 */

import squareLightSvg from './assets/brand/square-light.svg?url'
import squareDarkSvg from './assets/brand/square-dark.svg?url'
import horiLightSvg from './assets/brand/hori-light.svg?url'
import horiDarkSvg from './assets/brand/hori-dark.svg?url'
import owlLightSvg from './assets/brand/owl-light.svg?url'
import owlDarkSvg from './assets/brand/owl-dark.svg?url'

import squareLightPng from './assets/brand/square-light.png?url'
import squareDarkPng from './assets/brand/square-dark.png?url'
import horiLightPng from './assets/brand/hori-light.png?url'
import horiDarkPng from './assets/brand/hori-dark.png?url'
import owlLightPng from './assets/brand/owl-light.png?url'
import owlDarkPng from './assets/brand/owl-dark.png?url'

/** Breadcrumb parent ("À propos") target. */
export const ABOUT_URL = '/about/ulule/'

export type HeroCta = { label: string; href: string; variant: 'primary' | 'secondary' }

export const HERO_CTAS: HeroCta[] = [
  { label: 'Télécharger le logo', href: '#logos', variant: 'primary' },
  { label: 'Voir les stickers', href: '#stickers', variant: 'secondary' },
]

/** A logo variant's asset for each background: the version for light bg and the version for dark bg. */
type LogoVariantAssets = { light: string; dark: string }

/**
 * A downloadable logo. The card toggles between the light- and dark-background versions (previewed via
 * `svg`) and downloads the currently-shown one in the chosen format (PNG or SVG).
 */
export type Logo = {
  id: string
  name: string
  /** Display cap (px) inside the card stage — optical balance per the design. */
  maxWidth: number
  svg: LogoVariantAssets
  png: LogoVariantAssets
}

export const LOGOS: Logo[] = [
  {
    id: 'vertical',
    name: 'Vertical',
    maxWidth: 115,
    svg: { light: squareLightSvg, dark: squareDarkSvg },
    png: { light: squareLightPng, dark: squareDarkPng },
  },
  {
    id: 'horizontal',
    name: 'Horizontal',
    maxWidth: 230,
    svg: { light: horiLightSvg, dark: horiDarkSvg },
    png: { light: horiLightPng, dark: horiDarkPng },
  },
  {
    id: 'owl',
    name: 'Tête de chouette',
    maxWidth: 120,
    svg: { light: owlLightSvg, dark: owlDarkSvg },
    png: { light: owlLightPng, dark: owlDarkPng },
  },
]

/** A selectable variant of a sticker (its own label + downloadable assets). */
export type StickerVariant = { label: string; slug: string; png: string; svg: string }

export type Sticker = {
  name: string
  png: string
  svg: string
  /** When set, the card renders a picker that swaps the preview + downloads per variant. */
  variants?: StickerVariant[]
}
type StickerSeries = {
  num: string
  title: string
  description: string
  /** Numbered-badge foreground / background colours (per-series accent from the design). */
  badge: string
  badgeBg: string
  stickers: Sticker[]
}

// Eager `?url` globs pairing each sticker's PNG (preview + download) with its SVG (download), keyed by
// path. Resolving by `slug` keeps the series data below readable and fails loudly if an asset is missing.
const stickerPngByPath = import.meta.glob('./assets/stickers/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>
const stickerSvgByPath = import.meta.glob('./assets/stickers/*.svg', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

function resolveSticker(slug: string): { png: string; svg: string } {
  const png = stickerPngByPath[`./assets/stickers/${slug}.png`]
  const svg = stickerSvgByPath[`./assets/stickers/${slug}.svg`]
  if (png === undefined) {
    throw new Error(`Missing sticker PNG: ${slug}`)
  }
  if (svg === undefined) {
    throw new Error(`Missing sticker SVG: ${slug}`)
  }
  return { png, svg }
}

function sticker(slug: string, name: string): Sticker {
  return { name, ...resolveSticker(slug) }
}

/**
 * A sticker with selectable variants (e.g. "Objectif" at 100–500 %). The preview + downloads default
 * to the first variant and the card renders a picker — keeps the page from flooding with one card per
 * value, and generalises to the other variable-text stickers.
 */
function stickerWithVariants(name: string, variants: { label: string; slug: string }[]): Sticker {
  const resolved = variants.map((variant) => ({ ...variant, ...resolveSticker(variant.slug) }))
  return { name, png: resolved[0].png, svg: resolved[0].svg, variants: resolved }
}

export const STICKER_SERIES: StickerSeries[] = [
  {
    num: '01',
    title: 'Lancement / Bientôt lancé',
    description: "Créez l'attente autour de l'ouverture prochaine de votre collecte.",
    badge: '#00c8e0',
    badgeBg: '#e6fafd',
    stickers: [
      sticker('bientot-lance', 'Bientôt lancé'),
      sticker('lancement-imminent', 'Lancement imminent'),
    ],
  },
  {
    num: '02',
    title: 'Embarquez / Soutenez',
    description: 'Mobilisez votre communauté et invitez-la à soutenir votre projet.',
    badge: '#e8005a',
    badgeBg: '#ffe9ef',
    stickers: [
      sticker('rejoignez-la-campagne', 'Rejoignez la campagne'),
      sticker('je-soutiens-ce-projet', 'Je soutiens ce projet'),
      sticker('prolongations-en-cours', 'Prolongations en cours'),
    ],
  },
  {
    num: '03',
    title: 'Prochains objectifs / jalons',
    description:
      'Célébrez vos paliers et annoncez les prochaines étapes de votre campagne. Vous pouvez modifier les textes en utilisant les SVG.',
    badge: '#ffb800',
    badgeBg: '#fff9e2',
    stickers: [
      stickerWithVariants('Objectif', [
        { label: '100 %', slug: 'objectif-100' },
        { label: '150 %', slug: 'objectif-150' },
        { label: '200 %', slug: 'objectif-200' },
        { label: '250 %', slug: 'objectif-250' },
        { label: '300 %', slug: 'objectif-300' },
        { label: '350 %', slug: 'objectif-350' },
        { label: '400 %', slug: 'objectif-400' },
        { label: '450 %', slug: 'objectif-450' },
        { label: '500 %', slug: 'objectif-500' },
        { label: 'À personnaliser', slug: 'objectif' },
      ]),
      stickerWithVariants('Prochain palier', [
        { label: '150 %', slug: 'prochain-palier-150' },
        { label: '200 %', slug: 'prochain-palier-200' },
        { label: '250 %', slug: 'prochain-palier-250' },
        { label: '300 %', slug: 'prochain-palier-300' },
        { label: '350 %', slug: 'prochain-palier-350' },
        { label: '400 %', slug: 'prochain-palier-400' },
        { label: '450 %', slug: 'prochain-palier-450' },
        { label: '500 %', slug: 'prochain-palier-500' },
        { label: '600 %', slug: 'prochain-palier-600' },
        { label: 'À personnaliser', slug: 'prochain-palier' },
      ]),
      sticker('derniers-jours', 'Derniers jours !'),
      sticker('livre-avant-noel', 'Livré avant Noël'),
      stickerWithVariants('Livraison', [
        { label: 'Janvier', slug: 'livraison-en-janvier' },
        { label: 'Février', slug: 'livraison-en-fevrier' },
        { label: 'Mars', slug: 'livraison-en-mars' },
        { label: 'Avril', slug: 'livraison-en-avril' },
        { label: 'Mai', slug: 'livraison-en-mai' },
        { label: 'Juin', slug: 'livraison-en-juin' },
        { label: 'Juillet', slug: 'livraison-en-juillet' },
        { label: 'Août', slug: 'livraison-en-aout' },
        { label: 'Septembre', slug: 'livraison-en-septembre' },
        { label: 'Octobre', slug: 'livraison-en-octobre' },
        { label: 'Novembre', slug: 'livraison-en-novembre' },
        { label: 'Décembre', slug: 'livraison-en-decembre' },
        { label: 'À personnaliser', slug: 'livraison' },
      ]),
    ],
  },
  {
    num: '04',
    title: 'Succès',
    description: 'Fêtez votre collecte financée avec succès.',
    badge: '#08a777',
    badgeBg: '#e4fbf4',
    stickers: [
      sticker('finance-avec-succes', 'Financé avec succès'),
      sticker('projet-finance', 'Projet financé'),
    ],
  },
]

/** A few stickers shown as the hero montage — one per colour family, for visual balance. */
export const HERO_MONTAGE: Sticker[] = [
  sticker('lancement-imminent', 'Lancement imminent'),
  sticker('rejoignez-la-campagne', 'Rejoignez la campagne'),
  sticker('objectif-150', 'Objectif 150%'),
  sticker('projet-finance', 'Projet financé'),
]

type BestPractice = { kind: 'ok' | 'no'; title: string; description: string }

export const BEST_PRACTICES: BestPractice[] = [
  {
    kind: 'ok',
    title: 'Utilisez le logo officiel',
    description:
      "Téléchargez toujours la version officielle du logo Ulule depuis cette page. N'utilisez pas de captures d'écran ou de versions modifiées.",
  },
  {
    kind: 'ok',
    title: 'Respectez les proportions',
    description: "Ne déformez pas le logo. Redimensionnez-le en conservant ses proportions d'origine.",
  },
  {
    kind: 'ok',
    title: 'Préservez la lisibilité',
    description:
      'Assurez-vous que le logo reste lisible sur son fond. Utilisez la version claire sur fond sombre et la version sombre sur fond clair.',
  },
  {
    kind: 'ok',
    title: "Respectez l'espace de protection",
    description: "Laissez un espace libre autour du logo équivalent à la hauteur de la lettre « U » du logotype.",
  },
  {
    kind: 'no',
    title: 'Ne modifiez pas les couleurs',
    description:
      "N'altérez pas les couleurs du logo Ulule. Utilisez uniquement les versions officielles fournies.",
  },
  {
    kind: 'no',
    title: "Ne superposez pas d'éléments",
    description: "Ne placez pas d'éléments graphiques, de texte ou d'images par-dessus le logo.",
  },
]
